import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faBoxOpen, faXmark } from '@fortawesome/free-solid-svg-icons';
import useErrorHandling from '../../../../hooks/useErrorHandling';
import ProductStatesFobs from './StateFobs';
import { ProductPricingSchema } from '../../../../utils/validations';
import AdminService from '../../../../services/admin.service';
import { hideLoading, showLoading } from '../../../../lib/uiService';
import { useAuth } from '../../../../providers/AuthProvider';
import { UserRole } from '../../../../lib/types';
import { CurrencyTypes } from '../../../../constants';
import { useSearchParams } from 'react-router-dom';

const ProductTabs = ({
  selectedObject,
  fetchProducts,
  productHasHistory,
  setProductHasHistory,
  setSelectedObject,
  editable,
  setEditable,
  territories,
  statePrices,
  initialStatePricesData,
  setStatePrices,
  setProductHistory,
  relatedProducts,
  setRelatedProducts,
  setInitialRelatedProducts,
  disabled,
  products,
  brandFamilies,
  brands,
}: {
  selectedObject: any;
  productHasHistory: boolean;
  setProductHasHistory: (x: boolean) => void;
  fetchProducts: () => void;
  setSelectedObject: (x: any) => void;
  setEditable: (x: any) => void;
  editable: boolean;
  disabled: boolean;
  labels: any;
  territories: any;
  products: any;
  brandFamilies: any;
  brands: any;
  productHistory: any;
  statePrices: any;
  initialStatePricesData: any;
  setStatePrices: (x: any) => void;
  relatedProducts: any;
  setRelatedProducts: (x: any) => void;
  initialRelatedProducts: any;
  setInitialRelatedProducts: (x: any) => void;
  setProductHistory: (x: any) => void;
}) => {
  const { setError } = useErrorHandling();
  const { role, id } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [deletedTerritoryPrices, setDeletedTerritoryPrices] = useState<any>([]);
  const [createdTerritoryPrices, setCreatedTerritoryPrices] = useState<any>([]);
  const [editedTerritoryPrices, setEditedTerritoryPrices] = useState<any>([]);
  const [statesPricesChanged, setStatesPricesChanged] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [tiersAreValid, setTiersAreValid] = useState(false);
  const [searchParams] = useSearchParams();
  const paramProductHistoryGroupId = searchParams.get('phg');

  useEffect(() => {
    setStatesPricesChanged(checkFieldsChange(initialStatePricesData, statePrices, 'name'));
    setTiersAreValid(checktTiers());
  }, [statePrices, relatedProducts]);

  const checkFieldsChange = (initState, state, field) => {
    const sortArray = (arr) => {
      return arr.slice().sort((a, b) => a[field]?.localeCompare(b[field]));
    };

    // sort arrays so that the indexes match each territory
    const sortedInitialData = sortArray(initState);
    const sortedCurrentData = sortArray(state);

    if (sortedCurrentData.length !== sortedInitialData.length) {
      return true;
    }

    return sortedCurrentData.some((currentItem, index) => {
      const initialItem = sortedInitialData[index];
      return JSON.stringify(currentItem) !== JSON.stringify(initialItem);
    });
  };

  const checktTiers = () => {
    return statePrices.some(
      (terr) =>
        (terr.tier1Price !== null && terr.tier1Price !== '' && Number(terr.tier1Price) <= 0) ||
        (terr.tier2Price !== null && terr.tier2Price !== '' && Number(terr.tier2Price) <= 0),
    );
  };

  const onSubmit = async (values) => {
    const territoryHasNoPrice = statePrices.some((item: any) => Number(item.price) <= 0);
    if (territoryHasNoPrice) {
      setErrorMessage('All territories must have a price greater than 0.');
    } else {
      setErrorMessage('');
      const productData = {
        productHistoryGroupId: paramProductHistoryGroupId,
        productPrice: {
          deletedTerritoryPrices,
          createdTerritoryPrices,
          editedTerritoryPrices,
        },
      };
      showLoading();
      // if there's an open request and the user is a pricing admin
      if (productHasHistory && values.sendApproval) {
        console.log('Submitting approval request...', productData, values);
        // check approval count before submitting
        // AdminService.GetApprovalCount({ productHistoryId: selectedObject.productHistoryId })
        //   .then(({ data }) => {
        //     if (Number(data.approvalCount) >= Number(process.env.REACT_APP_PRICING_APPROVAL_COUNT)) {
        //       hideLoading();
        //       toast.warning('This request has been approved');
        //       return;
        //     } else {
        //     }
        //   })
        //   .catch((error) => {
        //     setError({ status: error.response.status || 500 });
        //   });
        AdminService.AddPricingApprovalAndCheckRequestState(productData)
          .then(({ data }) => {
            setEditable(false);
            setProductHasHistory(false);
            setRelatedProducts([]);
            setInitialRelatedProducts([]);
            setSelectedObject({});
            setProductHistory([]);
            setDeletedTerritoryPrices([]);
            setCreatedTerritoryPrices([]);
            setEditedTerritoryPrices([]);
            setIsApproved(true);
            hideLoading();
            toast.success(
              data.wasApproved
                ? 'The request has been approved'
                : 'You have approved these changes. Awaiting more approvals.',
            );
            fetchProducts();
          })
          .catch((e) => {
            hideLoading();
            setError(e.status ? e : { status: 500 });
          });
      } else {
        // if there's an open request, update it
        if (productHasHistory) {
          AdminService.UpdateProductHistory(productData)
            .then(() => {
              setEditable(false);
              setProductHasHistory(false);
              setRelatedProducts([]);
              setInitialRelatedProducts([]);
              setSelectedObject({});
              setProductHistory([]);
              setDeletedTerritoryPrices([]);
              setCreatedTerritoryPrices([]);
              setEditedTerritoryPrices([]);
              hideLoading();
              toast.success('The request has been updated.');
            })
            .catch((e) => {
              hideLoading();
              setError(e.status ? e : { status: 500 });
            });

          // otherwise create a request
        } else {
          AdminService.RequestProductPricingApproval(productData)
            .then(() => {
              // setEditable(false);
              // setProductHasHistory(false);
              // setRelatedProducts([]);
              // setInitialRelatedProducts([]);
              // setSelectedObject({});
              // setProductHistory([]);
              // setDeletedTerritoryPrices([]);
              // setCreatedTerritoryPrices([]);
              // setEditedTerritoryPrices([]);
              hideLoading();
              toast.success('The changes request has been sent for approval.');
              // fetchProducts();
            })
            .catch((e) => {
              hideLoading();
              setError(e.status ? e : { status: 500 });
            });
        }
      }
    }
  };

  const normalizeValue = (value: string) => {
    const num = Number(value);
    if (typeof value == 'boolean') {
      return value;
    }
    if (isNaN(num)) {
      return value;
    }
    return parseFloat(value).toString();
  };

  const { id: userId } = useAuth();
  const isPending = statePrices.some((price) => {
    const hasUserApproval = price.product?.approvals?.some((approval) => approval.createdBy === userId);
    return price.product?.approvalStatus === 'Pending' && !hasUserApproval;
  });

  return (
    <div className="card shadow mb-[40px] p-0">
      <div className="card-header d-flex justify-content-between">
        <h5 className="text-primary m-0 fw-bold pt-1 pb-1 d-flex align-items-center">
          <FontAwesomeIcon icon={faBoxOpen} className="me-2" style={{ fontSize: '30px' }} />
          State Product Pricing
        </h5>
        {editable && (
          <div className="pt-2 px-3 cursor-pointer">
            <FontAwesomeIcon
              icon={faXmark}
              onClick={() => {
                setEditable(false);
                setProductHasHistory(false);
                setSelectedObject({ label: selectedObject.label, labelId: selectedObject.labelId });
                setStatePrices([]);
              }}
            />
          </div>
        )}
      </div>
      <div className="card-body">
        <Formik
          enableReinitialize
          initialValues={{
            tier1Price: selectedObject?.tier1Price || '',
            tier2Price: selectedObject?.tier2Price || '',
            onPremisePrice: selectedObject?.onPremisePrice || '',
            offPremisePrice: selectedObject?.offPremisePrice || '',
            caseOnePrice: selectedObject?.caseOnePrice || '',
            sendApproval: false,
            isFrozen: true,
          }}
          validationSchema={ProductPricingSchema}
          onSubmit={onSubmit}
        >
          {({ errors, setFieldValue, handleSubmit, values, initialValues, setSubmitting }) => {
            const isDirty = () => {
              return Object.keys(initialValues).some((key) => {
                const initialValue = normalizeValue(initialValues[key]);
                const currentValue = normalizeValue(values[key]);
                return initialValue !== currentValue;
              });
            };

            return (
              <Form>
                {productHasHistory && isPending && (
                  <>
                    <div role="alert" className="fade alert-outline alert alert-primary alert-dismissible show">
                      <button type="button" className="btn-close d-none"></button>
                      <div className="alert-icon d-flex align-items-center justify-content-center">
                        <FontAwesomeIcon icon={faBell} />
                      </div>
                      <div className="alert-message">
                        <strong>There are pending product pricing updates awaiting your approval.&nbsp;</strong> Click
                        to approve.
                        <button
                          disabled={!role?.includes(UserRole.PricingAdmin) && !role?.includes(UserRole.Admin)}
                          className="btn btn-success mx-2"
                          onClick={(event) => {
                            event.preventDefault();
                            setFieldValue('sendApproval', true);
                            handleSubmit();
                          }}
                          style={{ textAlign: 'right' }}
                        >
                          Approve
                        </button>
                        <button
                          disabled={!role?.includes(UserRole.PricingAdmin) && !role?.includes(UserRole.Admin)}
                          className="btn btn-danger mx-2"
                          onClick={(event) => {
                            event.preventDefault();
                            AdminService.RejectPricingApproval({
                              referenceId: paramProductHistoryGroupId,
                              isProductApproval: true,
                            })
                              .then(() => {
                                setEditable(false);
                                setProductHasHistory(false);
                                setSelectedObject({});
                                setProductHistory([]);
                                setDeletedTerritoryPrices([]);
                                setCreatedTerritoryPrices([]);
                                setEditedTerritoryPrices([]);
                                hideLoading();
                                toast.success('The request has been rejected.');
                              })
                              .catch((e) => {
                                hideLoading();
                                setError(e.status ? e : { status: 500 });
                              });
                          }}
                          style={{ textAlign: 'right' }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </>
                )}
                <div>
                  <ProductStatesFobs
                    productHasHistory={productHasHistory}
                    territories={territories}
                    products={products}
                    brandFamilies={brandFamilies}
                    brands={brands}
                    statePrices={statePrices}
                    setStatePrices={setStatePrices}
                    deletedTerritoryPrices={deletedTerritoryPrices}
                    setDeletedTerritoryPrices={setDeletedTerritoryPrices}
                    createdTerritoryPrices={createdTerritoryPrices}
                    setCreatedTerritoryPrices={setCreatedTerritoryPrices}
                    editedTerritoryPrices={editedTerritoryPrices}
                    setEditedTerritoryPrices={setEditedTerritoryPrices}
                    natlPrice={values.natlFob}
                  />
                </div>
                {!productHasHistory && !isApproved && (
                  <div>
                    <p className="text-danger col d-flex justify-content-end">{errorMessage}</p>
                    <p className="text-danger col d-flex justify-content-end">
                      {Object.keys(errors).length > 0 &&
                        !role?.includes(UserRole.ProductUser) &&
                        'There are missing fields that need to be completed.'}
                    </p>
                    <div className="col d-flex justify-content-end">
                      <button
                        disabled={(!isDirty() && !statesPricesChanged) || tiersAreValid}
                        className="btn btn-primary float-end"
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          if (role?.includes(UserRole.ProductUser)) {
                            setSubmitting(true);
                            onSubmit(values);
                            setSubmitting(false);
                          } else {
                            setFieldValue('sendApproval', false);
                            handleSubmit();
                          }
                        }}
                        style={{ textAlign: 'right' }}
                      >
                        Submit for Approval
                      </button>
                    </div>
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default ProductTabs;
